import apiClient from '../axios.js';

export const memberTierService = {
  /**
   * 查詢會員等級列表
   * @param {Object} params - 查詢參數
   * @param {boolean} params.isActive - 是否啟用（選填）
   * @param {string} params.sortBy - 排序欄位（選填）
   * @param {string} params.order - 排序方向（選填）
   * @returns {Promise<Object>} 會員等級列表
   */
  getTiers: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.order) queryParams.append('order', params.order);

    const queryString = queryParams.toString();
    const url = `/membership/tiers${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response;
  },

  /**
   * 查詢會員等級詳情
   * @param {number} tierId - 會員等級 ID
   * @returns {Promise<Object>} 會員等級詳情
   */
  getTier: async (tierId) => {
    const response = await apiClient.get(`/membership/tiers/${tierId}`);
    return response;
  },

  /**
   * 創建會員等級
   * @param {Object} tierData - 會員等級資料
   * @returns {Promise<Object>} 創建結果
   */
  createTier: async (tierData) => {
    const response = await apiClient.post('/membership/tiers', tierData);
    return response;
  },

  /**
   * 更新會員等級
   * @param {number} tierId - 會員等級 ID
   * @param {Object} tierData - 會員等級資料
   * @returns {Promise<Object>} 更新結果
   */
  updateTier: async (tierId, tierData) => {
    const response = await apiClient.put(`/membership/tiers/${tierId}`, tierData);
    return response;
  },

  /**
   * 刪除會員等級
   * @param {number} tierId - 會員等級 ID
   * @returns {Promise<Object>} 刪除結果
   */
  deleteTier: async (tierId) => {
    const response = await apiClient.delete(`/membership/tiers/${tierId}`);
    return response;
  },

  /**
   * 計算會員應屬等級
   * @param {string} memberId - 會員 ID
   * @returns {Promise<Object>} 計算結果
   */
  calculateTier: async (memberId) => {
    const response = await apiClient.post(`/membership/tiers/calculate/${memberId}`);
    return response;
  },

  /**
   * 升級會員等級
   * @param {string} memberId - 會員 ID
   * @returns {Promise<Object>} 升級結果
   */
  upgradeTier: async (memberId) => {
    const response = await apiClient.post(`/membership/tiers/upgrade/${memberId}`);
    return response;
  },

  /**
   * 降級會員等級
   * @param {string} memberId - 會員 ID
   * @param {string} targetTierCode - 目標等級代碼
   * @returns {Promise<Object>} 降級結果
   */
  downgradeTier: async (memberId, targetTierCode) => {
    const response = await apiClient.post(`/membership/tiers/downgrade/${memberId}`, {
      targetTierCode
    });
    return response;
  },

  /**
   * 更新等級權益
   * @param {number} tierId - 會員等級 ID
   * @param {string} benefits - 權益列表（多行文字）
   * @returns {Promise<Object>} 更新結果
   */
  updateTierBenefits: async (tierId, benefits) => {
    const response = await apiClient.put(`/membership/tiers/${tierId}/benefits`, {
      benefits
    });
    return response;
  },

  /**
   * 查詢降等前提醒發送紀錄（分頁）
   * @param {Object} params - 查詢參數
   * @param {number} params.page - 頁碼
   * @param {number} params.pageSize - 每頁筆數
   * @param {string} params.settlementDate - 結算日篩選（選填）
   * @param {string} params.status - 狀態篩選 success / failed（選填）
   * @param {string} params.search - 會員名稱／電話搜尋（選填）
   * @returns {Promise<Object>} 提醒紀錄分頁列表
   */
  getReminderLogs: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('pageSize', params.pageSize);
    if (params.memberId) queryParams.append('memberId', params.memberId);
    if (params.settlementDate) queryParams.append('settlementDate', params.settlementDate);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString();
    const url = `/membership/tiers/reminder-logs${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response;
  },

  /**
   * 上傳會員卡圖片到 S3
   * @param {File} file - 圖片檔案
   * @returns {Promise<Object>} 上傳結果（含 fileUrl）
   */
  uploadCardImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response;
  }
};
